import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, onSnapshot, updateDoc, increment, collection, addDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './AuthContext';

interface CollectiveFundsContextType {
  totalFunds: number;
  projectedFunds: number;
  activeParticipants: number;
  currentInvestment: {
    totalAmount: number;
    participantsCount: number;
    isActive: boolean;
  };
  updateFunds: (amount: number) => Promise<void>;
  addParticipant: (userId: string, amount: number) => Promise<void>;
  incrementDrawCount: () => void;
  setProjectedDeduction: (amount: number) => void;
}

const CollectiveFundsContext = createContext<CollectiveFundsContextType | undefined>(undefined);

interface CurrentInvestment {
  totalAmount: number;
  participantsCount: number;
  isActive: boolean;
}

export function CollectiveFundsProvider({ children }: { children: React.ReactNode }) {
  const [currentInvestment, setCurrentInvestment] = useState({
    totalAmount: 0,
    participantsCount: 0,
    isActive: true
  });
  const { user } = useAuth();

  useEffect(() => {
    // Écouter les changements sur le document current
    const unsubscribe = onSnapshot(
      doc(db, 'collectiveInvestments', 'current'),
      (doc) => {
        if (doc.exists()) {
          const data = doc.data();
          setCurrentInvestment({
            totalAmount: data.totalAmount ?? 0,
            participantsCount: data.participantsCount ?? 0,
            isActive: data.isActive ?? true
          } as CurrentInvestment);
        }
      }
    );

    return () => unsubscribe();
  }, []);

  const addParticipant = async (userId: string, amount: number) => {
    try {
      // Ajouter le participant
      await addDoc(collection(db, 'collectiveInvestments', 'participants'), {
        userId,
        participationDate: new Date(),
        amount,
        isActive: true
      });

      // Mettre à jour les totaux
      const currentRef = doc(db, 'collectiveInvestments', 'current');
      await updateDoc(currentRef, {
        totalAmount: increment(amount),
        participantsCount: increment(1)
      });
    } catch (error) {
      console.error('Erreur lors de l\'ajout du participant:', error);
      throw error;
    }
  };

  return (
    <CollectiveFundsContext.Provider value={{
      totalFunds: currentInvestment.totalAmount,
      projectedFunds: currentInvestment.totalAmount,
      activeParticipants: currentInvestment.participantsCount,
      currentInvestment,
      updateFunds: async (amount: number) => {
        if (!user) throw new Error('User must be authenticated');
        
        const currentRef = doc(db, 'collectiveInvestments', 'current');
        await updateDoc(currentRef, {
          totalAmount: increment(amount)
        });
      },
      addParticipant,
      incrementDrawCount: () => {},
      setProjectedDeduction: () => {}
    }}>
      {children}
    </CollectiveFundsContext.Provider>
  );
}

export function useCollectiveFunds() {
  const context = useContext(CollectiveFundsContext);
  if (!context) {
    throw new Error('useCollectiveFunds must be used within CollectiveFundsProvider');
  }
  return context;
}